export default defineNuxtPlugin(() => {
  const officeReady = ref(false)
  const officeToken = ref<string | null>(null)

  // Load Office.js on the /addin route (iframe on Windows, WKWebView on Mac)
  const isAddinRoute = window.location.pathname === '/addin'
  const officeLoading = ref(isAddinRoute)

  if (!isAddinRoute) {
    return {
      provide: {
        officeReady,
        officeToken,
        officeLoading,
        officeSetToken: (_token: string) => {},
        wordRun: async () => {
          console.warn('[Marcus] Not running inside Word')
        },
      },
    }
  }

  // Note: __session cookie is HttpOnly so we can't read it from JS.
  // The Word add-in bootstraps auth via officeSetToken() after inline login,
  // and useAuthFetch sends the token as an Authorization header.

  // Safety timeout — if Office.js never loads, stop blocking the app
  const loadingTimeout = setTimeout(() => {
    if (officeLoading.value) {
      officeLoading.value = false
      console.warn('[Marcus] Office.js loading timeout (5s), unblocking app')
    }
  }, 5000)

  // Office.js is loaded statically via useHead in addin.vue (must be in <head> before page init
  // or WKWebView on Mac enters a reload loop). We just need to wait for it and call onReady.
  const Office = (window as any).Office
  if (Office) {
    Office.onReady((info: { host: string; platform: string }) => {
      clearTimeout(loadingTimeout)
      officeLoading.value = false
      if (info.host === 'Word' || info.host === Office.HostType?.Word) {
        officeReady.value = true
        console.log('[Marcus] Office.onReady — Word detected, platform:', info.platform)
      }
    })
  } else {
    // Office.js not yet loaded — poll briefly until it appears
    let attempts = 0
    const poll = setInterval(() => {
      attempts++
      const O = (window as any).Office
      if (O) {
        clearInterval(poll)
        O.onReady((info: { host: string; platform: string }) => {
          clearTimeout(loadingTimeout)
          officeLoading.value = false
          if (info.host === 'Word' || info.host === O.HostType?.Word) {
            officeReady.value = true
            console.log('[Marcus] Office.onReady — Word detected, platform:', info.platform)
          }
        })
      } else if (attempts > 50) {
        clearInterval(poll)
        officeLoading.value = false
        console.warn('[Marcus] Office.js never appeared on window')
      }
    }, 100)
  }

  // Set officeToken directly (called after inline login in task pane)
  function officeSetToken(token: string) {
    officeToken.value = token
  }

  return {
    provide: {
      officeReady,
      officeToken,
      officeLoading,
      officeSetToken,
      wordRun: async (fn: (context: any) => Promise<void>) => {
        const Word = (window as any).Word
        if (!Word) {
          console.warn('[Marcus] Word API not available')
          return
        }
        await Word.run(fn)
      },
    },
  }
})
